import S from 's-js';
import { lift } from 's-array';
import * as Promise from 'bluebird';
import Currency from 'lib/Currency';
import { IAjax } from 'lib/Ajax';
import TZDate from 'lib/TZDate';

import { Entrypoints } from 'core/app/entrypoints';

import { ApprovedCharge } from 'data/models/ApprovedCharge';
import { BillItem } from 'data/models/BillItem';
import { TotalPriceForCharges } from 'data/models/Charge';
import { DraftCharge } from 'data/models/DraftCharge';
import { PilotJobOrder } from 'data/models/PilotJobOrder';
import { PilotJobOrderDetailsBillingOptions } from 'data/models/PilotJobOrderDetailsBilling';

import { BillExportSettings, BillExportSettingsParams } from 'portcall/billexportsettings/model';

import { PilotJobOrderDetailsCache } from './cache';
import { PilotJobOrderViewModel } from './model';

export { BillingStatus } from 'portcall/billexportsettings/model';

export interface PilotageChargesParams {
    PJO : PilotJobOrderViewModel;
    PersistedPJO : PilotJobOrder;
    PilotageChargesFlipActive : () => boolean;
}

export type Charge = DraftCharge | ApprovedCharge | BillItem;

// TH 05/03/2017 -- For now, rounding all prices to nearest cent.
// This aligns w/ what QuickBooks requires.
// In the future, I could see needing to make this configurable.
export const PRICE_PRECISION = 2;

export function PilotageCharges(
    ajax : IAjax,
    entrypoints : Entrypoints,
    pjoCache : PilotJobOrderDetailsCache,
    billExportSettingsModel : (params : BillExportSettingsParams) => BillExportSettings,
    { PJO, PersistedPJO, PilotageChargesFlipActive } : PilotageChargesParams
) {
    const billingModel = pjoCache.PilotJobOrderDetailsBillings.update1(null, { Id: PJO.Model.Id() }),
        billingSystems = () => PJO.Model.PilotAgency() && PJO.Model.PilotAgency()!.Organization() ?
            PJO.Model.PilotAgency()!.Organization()!.BillingSystems() : [],
        // TH 05/03/2017 -- For now, the front end will only support having exactly one BillingSystem.
        // Our Db schema and server endpoints all support having multiple BillingSystems.
        // So longer term, we'd just need to change the client to have a BillingSystem dropdown or some such.
        billingSystem = S(() => billingSystems().length > 0 ? billingSystems()[0] : null),
        billExportSettings = billExportSettingsModel({
            DraftModel: PJO.Model,
            LastExportDate: billingModel.LastExportDate,
            ApproveAndRecalculate: {
                PersistedModel: PersistedPJO,
                BillingSystem: billingSystem,
                DraftChargeCollections: pjoCache.DraftChargeCollections,
                JobComplete: () =>
                    // The BillExportSettings component uses JobComplete to determine whether to enable the
                    // Approve for Export button.
                    // The ApprovePilotJobOrderCharges endpoint requires that the PJO be complete or cancelled.
                    // So in the non-cancelled case it's important we make sure the PJO actual times have been
                    // persisted before we enable the Approve for Export button.
                    !!PJO.Model.Cancellation() || (
                        !!PersistedPJO.Start.Actual() &&
                        !!PersistedPJO.End.Actual() &&
                        !!PJO.Model.Start.Actual() &&
                        !!PJO.Model.End.Actual() &&
                        TZDate.eq(PersistedPJO.Start.Actual()!, PJO.Model.Start.Actual()!) &&
                        TZDate.eq(PersistedPJO.End.Actual()!, PJO.Model.End.Actual()!)
                    ),
                ApproveEndPoint: entrypoints.ApprovePilotJobOrderCharges,
                RecalculateEndPoint: entrypoints.RecalculatePilotJobOrderCharges
            }
        }),
        bill = S(() => PJO.Model.Bills().length > 0 ? PJO.Model.Bills()![0].Bill() : null),
        chargesList = lift(() =>
            // TH 05/15/2017 -- Update: removed the branch for showing bill().Items().
            // bill().Items() includes charges for all PJOs that were grouped into the same
            // invoice. That's not what we want here.
            PJO.Model.ApprovalOfCharges() ? PJO.Model.ApprovalOfCharges()!.Charges() :
            billingModel.DraftCharges() ? billingModel.DraftCharges()!.Charges() :
            []
        ),
        defaultCurrency = () => billingSystem() && billingSystem()!.Currency() || Currency.USD,
        invoiceTotal = TotalPriceForCharges(chargesList, defaultCurrency, PRICE_PRECISION),
        refreshClient = ajax.Url<PilotJobOrderDetailsBillingOptions>(() =>
            entrypoints.PilotJobOrderDetailsBilling({ BillingSystemId: billingSystem()!.Id(), Id: PJO.Model.Id() })),
        loading = S.data(false);

    let lastRefreshRequest = Promise.Promise.resolve(),
        refreshAlreadyQueued = false;

    S(() => {
        // TH 05/04/2017 -- UPDATE: Tolerate a missing billingSystem instead of throwing.
        // Don't fetch billing data until we find a BillingSystem.
        // This is to prevent a race condition fetching the BillingSystems list in the event
        // the user loads the site on the PJO details billing flip.
        if (PilotageChargesFlipActive() && billingSystem()) {
            // TH 05/16/2017 -- Subscribe to other values that we expect might influence the
            // draft charges. This is so that if the user flips to the Pilotage Charges while
            // an auto-save is in flight, we can refresh the draft charges after the auto-save
            // completes.
            // NOTE: Subscribing to both draft and persisted values. It's somewhat redundant,
            // but gives us the best chance of being notified once an auto-save is finished.
            PersistedPJO.DeepDraft.Num(); // DeepDraft affects draft charges
            PJO.Model   .DeepDraft.Num();
            PersistedPJO.DeepDraft.UOM();
            PJO.Model   .DeepDraft.UOM();
            PersistedPJO.EffectiveVessel(); // EffectiveVessel affects Tonnage charges
            PJO.Model   .EffectiveVessel();
            PersistedPJO.Pilots(); // Pilots appear in description of various charges
            PersistedPJO.FromPoint() && PersistedPJO.FromPoint()!.Location(); // From and To Location appear in description of various charges
            PJO.FromPoint() && PJO.FromPoint()!.Model.Location();
            PersistedPJO.ToPoint() && PersistedPJO.ToPoint()!.Location();
            PJO.ToPoint() && PJO.ToPoint()!.Model.Location();

            if (!refreshAlreadyQueued) {
                refreshAlreadyQueued = true;
                lastRefreshRequest.finally(() => {
                    refreshAlreadyQueued = false;
                    lastRefreshRequest = sendRefreshRequest();
                });
            }
        }
    });

    return {
        Model: billingModel,
        // TH 05/10/2017 -- If we're waiting on a BillingSystem, also count that as loading
        Loading: () => !billingSystem() || loading(),
        JobOrder: PJO.Model,
        Bill: bill,
        BillExportSettings: billExportSettings,
        BillingStatus: billExportSettings.BillingStatus,
        ChargesList: chargesList,
        InvoiceTotal: invoiceTotal
    };

    function sendRefreshRequest() {
        const req = refreshClient.Get();
        loading(true);
        return req.then(billingData => {
            pjoCache.PilotJobOrderDetailsBillings.update1(req.Date, billingData);
        }).finally(() => loading(false));
    }
}
export type PilotageCharges = ReturnType<typeof PilotageCharges>;


// WEBPACK FOOTER //
// ./apps/pilots/pilotjoborderdetails/pilotagecharges.ts